import { Fragment, useCallback, useEffect, useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import 'katex/dist/katex.min.css'
import { useMutation } from '@apollo/client'
import { useUserData } from '@nhost/react'
import { useAudio } from './AudioContext'
import { SAVE_PROGRESS } from './api'
import { getChapterImageUrl } from '@/shared/lib/imageUtils'
import { UserAudioPlayer } from './UserAudioPlayer'
import type { Article } from './types'
import { Paywall, usePaidState, useSciencePaywall } from '@/features/payments'
import { useProgress } from './useProgress'
import { useReadingPreferences } from '@/shared/hooks/useReadingPreferences'
import { getChapterTotalDuration } from '@/shared/lib/formatDuration'
import { ArticleCards, ArticleCompleteButton, ChapterIntroCard, ReadingCards } from './ArticleCards'
import { useReadingListNav, type ReadingList } from './useReadingListNav'
import {
  chapterCoverAlt,
  chapterCoverImageId,
  type ChapterCoverArticle,
} from '@/shared/lib/chapterCover'

type ShortChapterInfo = {
  title: string
  subtitle?: string | null
  hook?: string | null
  summary?: string | null
  takeaway?: string | null
  duration?: number | null
  cover?: ChapterCoverArticle[] | null
}

type ShortChapterReadingContentProps = {
  chapter: ShortChapterInfo
  articles: Article[]
  chapterSlug: string
  isOpen: boolean
  /** Startet das Audio direkt - gesetzt, wenn das Kapitel über einen Playknopf geöffnet wurde. */
  autoPlay?: boolean
  /** Liste, aus der man gekommen ist - blättert unter dem Player weiter. */
  readingList?: ReadingList
  /** Fallback ohne Artikelliste: zum Nachbarkapitel. */
  onBeyondPrev?: () => void
  onBeyondNext?: () => void
  /** Gespeicherte Hörposition dieses Kapitels, in Sekunden. */
  initialAudioPosition?: number | null
  onClose: () => void
}

export function ShortChapterReadingContent({
  chapter,
  articles,
  chapterSlug,
  isOpen,
  autoPlay,
  readingList,
  onBeyondPrev,
  onBeyondNext,
  initialAudioPosition,
  onClose,
}: ShortChapterReadingContentProps) {
  const user = useUserData()
  // Ein Kurzkapitel hat genau einen Artikel - die Zeile unter dem Player blaettert
  // deshalb immer in die Liste, nie innerhalb des Kapitels.
  const listNav = useReadingListNav(readingList, chapterSlug, articles[0]?.slug ?? null)
  const { audioRef, currentArticle, isPlaying, shouldSaveProgress } = useAudio()
  const [saveProgressMutation] = useMutation(SAVE_PROGRESS)
  const articleRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // Kurzkapitel zeigen alle Artikel untereinander, deshalb loesen hier alle
  // Science-Abschnitte aus - nicht nur die eines einzelnen Artikels.
  const isPaid = usePaidState()
  const paywallOpacity = useSciencePaywall({
    isPaid,
    visibleArticles: articles,
    playingUnitKey: isPlaying ? (currentArticle?.unitKey ?? null) : null,
    articleRefs,
  })
  const paywallActive = isPaid === false && paywallOpacity >= 1

  // Hinter der Schranke darf nichts weiterlaufen. Der Player wird nur ausgeblendet,
  // angehalten wird hier - sonst liefe der Science-Abschnitt als Ton einfach weiter.
  useEffect(() => {
    if (paywallActive && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause()
    }
  }, [paywallActive, audioRef])
  const coverImageId = chapterCoverImageId(chapter)
  const imageUrl = coverImageId ? getChapterImageUrl(coverImageId) : null

  const { isArticleDone, isChapterDone, markArticleComplete, markChapterComplete } = useProgress()
  const { hideIntroSections, hideOutroSections } = useReadingPreferences()
  const readingPrefs = { hideIntroSections, hideOutroSections }
  const effectiveDuration = getChapterTotalDuration(
    { duration: chapter.duration, articles },
    readingPrefs
  )

  // Die Artikelliste kommt frisch aus dem Query des Elternteils und wechselt bei jedem
  // Render die Identität. Als Dependency taugt deshalb nur ihre Signatur.
  const articleSlugKey = articles.map((a) => a.slug).join(',')

  const completeArticle = useCallback(
    (slug: string) => {
      markArticleComplete(slug, chapterSlug)
    },
    [markArticleComplete, chapterSlug]
  )

  /**
   * Kurzkapitel haben keine Übersichtsseite, die den Stand mitschreiben könnte - sie
   * tun es hier selbst: beim Pausieren (über den Player) und beim Verlassen der Ansicht.
   */
  const saveProgress = useCallback(
    (params?: { articleSlug?: string; audioPosition?: number }) => {
      if (!shouldSaveProgress || !user?.id) return
      const articleSlug = params?.articleSlug ?? currentArticle?.slug ?? articles[0]?.slug ?? null
      const audioPosition = params?.audioPosition ?? audioRef.current?.currentTime ?? 0
      saveProgressMutation({
        variables: {
          userId: user.id,
          chapterSlug,
          articleSlug,
          audioPosition: Math.floor(audioPosition),
        },
      }).catch((err) => console.error('Failed to save progress:', err))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      shouldSaveProgress,
      user?.id,
      chapterSlug,
      currentArticle?.slug,
      articleSlugKey,
      audioRef,
      saveProgressMutation,
    ]
  )

  // Beim Verlassen der Ansicht den letzten Stand sichern. Über eine Ref, damit die
  // Aufräumfunktion nicht bei jeder Änderung neu aufgesetzt wird und trotzdem die
  // aktuellen Werte sieht.
  const saveProgressRef = useRef(saveProgress)
  useEffect(() => {
    saveProgressRef.current = saveProgress
  }, [saveProgress])
  useEffect(() => () => saveProgressRef.current(), [])

  // Kapitelabschluss wird abgeleitet, genau wie bei den langen Kapiteln. Ohne
  // Zeiger-Vorschub: Kurzkapitel liegen nicht in der linearen Kursreihenfolge.
  useEffect(() => {
    if (!isOpen) return
    const slugs = articles.map((a) => a.slug)
    if (slugs.length === 0) return
    if (isChapterDone(chapterSlug)) return
    if (!slugs.every((slug) => isArticleDone(slug))) return
    markChapterComplete(chapterSlug)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, articleSlugKey, chapterSlug, isArticleDone, isChapterDone, markChapterComplete])

  // Hook/Zusammenfassung/Takeaway leben pro Artikel. Bei genau einem Artikel im
  // Kapitel (der Normalfall im Tracking) zeigen wir dessen Werte, sonst bleibt
  // es beim gemeinsamen Kapitel-Wert.
  const soloArticle = articles.length === 1 ? articles[0] : null
  const overviewTitle = soloArticle?.title || chapter.title
  const overviewHook = soloArticle?.hook || chapter.hook
  const overviewSummary = soloArticle?.summary || chapter.summary
  const overviewTakeaway = soloArticle?.takeaway || chapter.takeaway
  const overviewSubtitle = soloArticle?.subtitle || chapter.subtitle

  return (
    <div
      className={`fixed inset-0 z-[100] bg-background flex flex-col transition-transform duration-300 ease-out ${
        isOpen ? 'translate-y-0' : 'translate-y-full'
      }`}
    >
      <div className="absolute top-safe left-0 right-0 z-50 flex justify-start px-4 py-3">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/50 dark:bg-card/80 backdrop-blur-md hover:bg-white/70 dark:hover:bg-card transition"
          aria-label="Schließen"
        >
          <ChevronDown className="w-6 h-6 text-primary dark:text-foreground" />
        </button>
      </div>

      <div
        className="flex-1 overflow-y-auto pt-14 pb-52"
        style={{ pointerEvents: paywallActive ? 'none' : 'auto' }}
      >
        <ReadingCards>
          <ChapterIntroCard
            title={overviewTitle}
            subtitle={overviewSubtitle}
            imageUrl={imageUrl}
            imageAlt={chapterCoverAlt(chapter)}
            hook={overviewHook}
            summary={overviewSummary}
            takeaway={overviewTakeaway}
            articleCount={articles.length}
            duration={effectiveDuration}
          />

          {articles.map((article) => (
            <Fragment key={article.slug}>
              <ArticleCards
                article={article}
                registerRef={(unitKey, el) => {
                  articleRefs.current[unitKey.toLowerCase()] = el
                }}
                readingPrefs={readingPrefs}
              />
              <ArticleCompleteButton
                done={isArticleDone(article.slug)}
                onComplete={() => completeArticle(article.slug)}
              />
            </Fragment>
          ))}
        </ReadingCards>
      </div>

      {isPaid === false && (
        <Paywall opacity={paywallOpacity} chapterSlug={chapterSlug} onBack={onClose} />
      )}

      <UserAudioPlayer
        articles={articles}
        readingPrefs={readingPrefs}
        visibleSlug={null}
        articleRefs={articleRefs}
        chapterSlug={chapterSlug}
        chapterTitle={chapter.title}
        initialAutoPlay={autoPlay}
        paywallVisible={paywallActive}
        initialAudioPosition={initialAudioPosition}
        onSaveProgress={saveProgress}
        onArticleCompleted={completeArticle}
        onBeyondPrev={listNav ? listNav.onBeyondPrev : onBeyondPrev}
        onBeyondNext={listNav ? listNav.onBeyondNext : onBeyondNext}
        listPosition={listNav?.listPosition ?? null}
      />
    </div>
  )
}
